import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCountrySelect from 'shared/hooks/useCountrySelect';
import useOrganizationSelect from 'shared/hooks/useOrganizationSelect';
import { useUploadTariffBookFlow } from './useUploadTariffBookFlow';
import { INITIAL_FORM_DATA } from '../components/modal-upload-tariff-book/modal-upload-tariff-book.constants';

export const useUploadTariffBookForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const fileInputRef = useRef();

  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [error, setError] = useState();
  const [hospitalSearch, setHospitalSearch] = useState('');

  const { handleUploadFlow, loading, dispatchFilePath, resetState } =
    useUploadTariffBookFlow();
  const {
    countries,
    countryLoading,
    handleSearchCountry,
    handleScrollCountry,
  } = useCountrySelect({ isEnabled: isOpen });
  const {
    organizations,
    organizationLoading,
    handleSearchOrganization,
    handleScrollOrganization,
  } = useOrganizationSelect({ isEnabled: isOpen });

  useEffect(() => {
    setFile(null);
    setError();
    setFormData(INITIAL_FORM_DATA);
    setHospitalSearch('');
  }, [isOpen]);

  const handleFileChange = useCallback(
    (e, isEdit = false) => {
      const value = isEdit ? e : e.file;
      setFile(value);
      dispatchFilePath(URL.createObjectURL(value));
      setFormData((prev) => ({ ...prev, file: value }));
      setError();
    },
    [dispatchFilePath]
  );

  const handleSelectCountry = useCallback((value) => {
    setFormData((prev) => ({ ...prev, country_id: value || null }));
  }, []);

  const handleOrganizationChange = useCallback((_value, option) => {
    const label = option?.label ?? _value;
    setFormData((prev) => ({ ...prev, organization_name: label || '' }));
    setError((prev) => ({ ...prev, organization_name: '' }));
  }, []);

  const handleOrganizationSearch = useCallback(
    (value) => {
      setHospitalSearch(value);
      handleSearchOrganization(value);
    },
    [handleSearchOrganization]
  );

  const handleOrganizationClear = useCallback(() => {
    setHospitalSearch('');
    handleSearchOrganization('');
  }, [handleSearchOrganization]);

  const handleStartingDateChange = useCallback((date) => {
    setFormData((prev) => ({ ...prev, starting_date: date }));
    setError((prev) => ({ ...prev, starting_date: '' }));
  }, []);

  const handleToggleDiscount = useCallback((checked) => {
    setFormData((prev) => ({
      ...prev,
      activate_discount_rule: checked,
      discount_rule: checked ? prev.discount_rule : '',
    }));
  }, []);

  const handleDiscountRuleChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, discount_rule: e.target.value }));
  }, []);

  const submit = useCallback(async () => {
    if (!formData?.organization_name) {
      setError({ organization_name: t('hospital_required') });
      return;
    }
    if (formData?.starting_date === '') {
      setError({ starting_date: t('start_date_required') });
      return;
    }
    if (formData?.file === null) {
      setError({ file: t('file_required') });
      return;
    }

    setError();
    const success = await handleUploadFlow(formData);
    if (success) {
      setFormData(INITIAL_FORM_DATA);
      setHospitalSearch('');
    }
  }, [formData, t, handleUploadFlow]);

  const closeModal = useCallback(() => {
    setFile(null);
    setFormData(INITIAL_FORM_DATA);
    resetState();
    onClose();
  }, [resetState, onClose]);

  return {
    file,
    formData,
    error,
    hospitalSearch,
    loading,
    fileInputRef,
    countries,
    countryLoading,
    organizations,
    organizationLoading,
    handleFileChange,
    handleSelectCountry,
    handleSearchCountry,
    handleScrollCountry,
    handleOrganizationChange,
    handleOrganizationSearch,
    handleOrganizationClear,
    handleScrollOrganization,
    handleStartingDateChange,
    handleToggleDiscount,
    handleDiscountRuleChange,
    submit,
    closeModal,
  };
};
